import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { containerVariants, cardVariants, fadeUp } from '../utils/variants';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';

const ERPModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    city: '',
    message: '',
    demoType: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const WHATSAPP_NUMBER = '919254147455';

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.dataset.scrollY = scrollY;
      document.body.style.overflow = 'hidden';
      window.scrollTo({ top: 0 });
    } else {
      document.body.style.overflow = 'auto';
      const prevScroll = document.body.dataset.scrollY || 0;
      window.scrollTo(0, prevScroll);
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const message = `
      *Demo Request Form Details:*\n
      *Name*: ${form.name}\n
      *Email*: ${form.email}\n
      *Phone*: ${form.phone}\n
      *School*: ${form.school}\n
      *City*: ${form.city}\n
      *Message*: ${form.message}\n
      *Demo Type*: ${form.demoType}
    `;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;

    try {
      window.open(whatsappUrl, '_blank');
      setSuccess('Message sent successfully!');
      setForm({
        name: '',
        email: '',
        phone: '',
        school: '',
        city: '',
        message: '',
        demoType: '',
      });
      setError('');
    } catch (err) {
      setError('Failed to send message. Please try again.');
      setSuccess('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center z-[9999] bg-black bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-gradient-to-br from-[#E0F2FE] to-[#FFFFFF] rounded-lg shadow-lg md:p-6 p-4 max-w-lg w-full relative"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, scale: 0.9 }}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-[#1E293B] text-2xl hover:text-[#0369A1] transition-all"
            >
              <FaTimes />
            </button>

            <div className="flex justify-between items-center mb-3">
              <h2 className="text-2xl font-semibold text-[#1E293B]">
                Request Demo Credentials
              </h2>
            </div>

            <p className="text-[#1E293B] md:text-left mb-3">
              Select a demo type and fill out the form to schedule your session!
            </p>

            <motion.div
              className="bg-white bg-opacity-70 p-4 md:p-5 rounded-lg shadow-inner"
              variants={cardVariants}
            >
              <form onSubmit={handleSubmit}>
                <label className="block mb-3">
                  <motion.input
                    type="text"
                    name="school"
                    value={form.school}
                    onChange={handleChange}
                    placeholder="Enter School Name"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>

                <label className="block mb-3">
                  <motion.input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter Name"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>

                <label className="block mb-3">
                  <motion.input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="Enter Email"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>

                <label className="block mb-3">
                  <motion.input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="Enter Phone Number"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>

                <label className="block mb-3">
                  <motion.input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="Enter City"
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  />
                </label>

                <label className="block mb-3">
                  <motion.select
                    name="demoType"
                    value={form.demoType}
                    onChange={handleChange}
                    className="w-full px-3 py-2 rounded-full border border-transparent bg-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    required
                    variants={fadeUp}
                  >
                    <option value="">Select Demo Type</option>
                    <option value="admin">Admin Demo</option>
                    <option value="student">Student Demo</option>
                    <option value="teacher">Teacher Demo</option>
                    <option value="library">Library Demo</option>
                  </motion.select>
                </label>

                <label className="block mb-3">
                  <motion.textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Enter Message"
                    className="w-full px-3 py-2 rounded-2xl border border-transparent bg-[#FFFFFF] focus:outline-none focus:ring-2 focus:ring-[#0284C7] shadow-sm"
                    rows="3"
                    required
                    variants={fadeUp}
                  ></motion.textarea>
                </label>

                {success && <p className="text-green-600 text-center mb-2">{success}</p>}
                {error && <p className="text-red-600 text-center mb-2">{error}</p>}

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0284C7] text-white py-3 rounded-full hover:bg-[#0369A1] transition duration-200 font-semibold"
                  variants={fadeUp}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ERPModal;
